import { useQuery } from '@tanstack/react-query';
import { useOrganizationStore } from '@/core/stores/organization.store';
import { projectsService, PROJECT_KEYS } from '@/features/projects/public';
import type { Project } from '@/features/projects/public';

/**
 * Returns the flat list of active projects for the current tenant.
 * Lives in shared/ so layout components (SidebarProjects, ProjectSwitcher)
 * can consume it without creating cross-feature imports.
 *
 * @remarks
 * For paginated/infinite use in the sidebar, use `useProjectsInfinite`.
 * staleTime 120s — projects change infrequently.
 *
 * @example
 * const { data: projects = [], isLoading } = useProjects()
 *
 * @param enabled - Defer the fetch until true (e.g. a closed dialog that only needs it once opened).
 */
export function useProjects(enabled = true): ReturnType<typeof useQuery<Project[]>> {
  const orgId = useOrganizationStore((s) => s.activeOrganizationId);

  return useQuery({
    queryKey: PROJECT_KEYS.list(orgId ?? ''),
    queryFn: async () => {
      const result = await projectsService.getList({
        IncludeArchived: false,
        Page: 1,
        PageSize: 100,
      });
      return result.items ?? [];
    },
    staleTime: 120_000,
    enabled: !!orgId && enabled,
  });
}
